/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState } from 'react';
import { Flip, toast } from 'react-toastify';
import { usePolesContext } from '../poles/PolesContext';
import { getAlerts, getLastData } from './_requests';
import { Alert, Data } from './_models';
import { content } from './_notification';
import { useAudio } from '../sound';
import { socket } from '../socket';

interface DataContextProps {
  connected?: Date;
  lastData?: Data;
  alerts: Alert[];
}

const DataContext = createContext<DataContextProps | undefined>(undefined);

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};

export function DataProvider({ children }: { children: React.ReactNode }) {
  const { currentPole } = usePolesContext();
  const [play] = useAudio();
  const [connected, setConnected] = useState<Date | undefined>();
  const [lastData, setLastData] = useState<Data | undefined>();
  const [alerts, setAlerts] = useState<Alert[]>([]);

  useEffect(() => {
    if (currentPole) {
      getLastData(currentPole.id).then(({ data }) => {
        setLastData(data);
        if (data?.mppt?._time) setConnected(new Date(data.mppt._time));
      });

      getAlerts(currentPole.id).then(({ data }) => setAlerts(data));

      socket.on('activity', onActivity);
      socket.on('data', onData);
    }

    return () => {
      socket.off('activity', onActivity);
      socket.off('data', onData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPole]);

  const onActivity = (data: { serial: string }) => {
    console.debug('activity', data);

    if (data.serial === currentPole?.serial) setConnected(new Date());
  };

  const onData = (updatedData: Data) => {
    console.debug('data updated', updatedData);

    if (updatedData) {
      // Update state
      setLastData((prevData?: Data) =>
        Object.assign(prevData ?? {}, updatedData)
      );

      // Update alerts
      const lastAlert = updatedData as Alert,
        keys = Object.keys(lastAlert).filter((k) => k !== '_time');
      if (!keys.length) return;

      const item = lastAlert[keys[0] as keyof Alert] as unknown as {
        trigger: string;
      };

      if (item.trigger && lastAlert._time) {
        play();

        setAlerts((prev) => [lastAlert, ...prev]);

        toast(content(lastAlert), {
          position: 'top-center',
          className: 'p-0',
          bodyClassName: 'p-0 opacity-100',
          closeOnClick: false,
          closeButton: false,
          autoClose: 60000,
          toastId: `${currentPole?.serial}-${keys[0]}-${item.trigger}`,
          transition: Flip,
        });
      }
    }
  };

  return (
    <DataContext.Provider value={{ connected, lastData, alerts }}>
      {children}
    </DataContext.Provider>
  );
}
